import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  EntityRelationDirection,
  IEntity,
  IEntityRelation,
} from '@app/core/services/http-services/model/entity.service';
import { ProdOrderService } from '@app/core/services/http-services/operative/prod-order.service';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { ChainingProdOrderDTO } from '@app/core/models/chaining-prodorderDTO';
import { NewProdOrderInputDTO } from '@app/core/models/new-prod-order-input-dto';
import { CreateChainProdOrderInput, ProductActivityDetails } from '@app/core/models/prod-order';
import { _, TranslateDirective, TranslatePipe } from "@ngx-translate/core";
import { Guid } from 'guid-typescript';
import { DialogRef, DialogContentBase, DialogTitleBarComponent, DialogActionsComponent } from '@progress/kendo-angular-dialog';
import { SVGIcon, warningTriangleIcon } from '@progress/kendo-svg-icons';
import { ProdOrderSequenceService } from '@app/core/services/http-services/operative/prod-order-sequence.service';
import { FormFieldComponent, NumericTextBoxComponent, CheckBoxDirective } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { DecimalPipe } from '@angular/common';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { ProdOrderStateHelper } from '@app/shared/helpers/prod-order-state.helper';
import { firstValueFrom } from 'rxjs';
import { ProdOrderRemoveDialogService } from '@app-module-operative/services/prod-order-edit.service';
import { ICopyPOPreviewParentPO } from '@app/core/models/operative/prod-order/copy-po-preview/copy-po-preview-parent-po.model';

_('Operative.CreateChainPOInProgress');

@Component({
    selector: 'app-create-multi-step-prod-order',
    templateUrl: './create-multi-step-prod-order.component.html',
    styleUrls: ['./create-multi-step-prod-order.component.css'],
    imports: [
    DialogTitleBarComponent,
    TranslateDirective,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    NumericTextBoxComponent,
    DropDownListComponent,
    SVGIconComponent,
    LoaderComponent,
    DialogActionsComponent,
    CheckBoxDirective,
    ButtonComponent,
    DecimalPipe,
    TranslatePipe
],
})
export class CreateMultiStepProdOrderComponent extends DialogContentBase implements OnInit {
  prod: ProductActivityDetails[] = null;
  productAndQty: ChainingProdOrderDTO[] = [];
  disableGenerateBtn = true;
  loading = false;
  useReportedQty: boolean = false;
  form: FormGroup = new FormGroup({});

  //Icons
  warningTriangleIcon: SVGIcon = warningTriangleIcon;

  private readonly prodOrderEditService = inject(ProdOrderRemoveDialogService);
  private readonly prodOrderService = inject(ProdOrderService);
  private readonly prodOrderSecuenceService = inject(ProdOrderSequenceService);
  private readonly appNotificationService = inject(AppNotificationService);

  constructor(
    @Inject(DialogRef) public data: CreateMultiStepData,
    private readonly dialogRef: DialogRef,
    private fb: FormBuilder
  ) {
    super(dialogRef);

    this.form = this.fb.group({
      numberOfSteps: this.fb.control(1),
      steps: this.fb.array([]),
    });
  }

  get steps() {
    return this.form.controls['steps'] as FormArray;
  }

  ngOnInit(): void {
    this.form.get('numberOfSteps').disable();

    const materialIndexes: Set<number> = new Set<number>();

    this.data.entityRelations
      .filter((d) => d.isChaining)
      .forEach((rel) => {
        if (!materialIndexes.has(rel.productIndex)) {
          materialIndexes.add(rel.productIndex);
          this.productAndQty.push(<ChainingProdOrderDTO>{
            plannedQty: rel.plannedQty,
            productIndex: rel.productIndex,
            reportedQty: rel.reportedQty,
          });
        }
      });

    this.prodOrderService
      .getActivityForMaterials(materialIndexes, EntityRelationDirection.CONSUMES, this.data.prodOrderId)
      .subscribe((res) => {
        for (const f of res) {
          this.addStepForm(f, this.getQty(f.productIndex), this.getReportedQty(f.productIndex), this.data.prodOrderId);
        }
        this.prod = res;
        this.disableGenerateBtn = false;
      });
  }

  async save() {

    if (this.data?.copyPOPreviewParentPOInfo != undefined && !ProdOrderStateHelper.postReleased(this.data?.copyPOPreviewParentPOInfo.prodOrderStateId) && !this.data?.copyPOPreviewParentPOInfo.externalProdOrderId) {
        await firstValueFrom(this.prodOrderEditService.showConfirmation([this.data?.copyPOPreviewParentPOInfo.prodOrderId]));
    }

    this.loading = true;
    this.disableGenerateBtn = true;

    const chainPOInput = <CreateChainProdOrderInput>{
      chainingProdorders: this.steps.value
        .filter((v) => v.activityIndex)
        .map(
          (v) =>
            <ChainingProdOrderDTO>{
              activityIndex: v.activityIndex,
              productIndex: v.productIndex,
              plannedQty: v.plannedQty,
              reportedQty: v.reportedQty,
            }
        ),
      newProdOrderInput: this.data.prodOrder,
      prodOrderId: this.data.prodOrderId,
      siteId: this.data.siteId,
      useReportedQty: this.useReportedQty,
      isEmptyStock: this.data.isEmptyStock,
    };

    this.prodOrderSecuenceService.createPOChain(chainPOInput).subscribe({
      next: (res) => {
        if (res) {
          this.appNotificationService.notifyInfoAppChanel('Operative.CreateChainPOInProgress');
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.disableGenerateBtn = false;
      },
    });
  }

  close() {
    this.dialogRef.close(false);
  }

  getControl(i: number, name: string) {
    return this.steps.at(i).get(name) as FormControl;
  }

  getQty(productIndex: number): number {
    return this.productAndQty.find((p) => p.productIndex === productIndex)?.plannedQty ?? 0;
  }

  getBatchSize(i: number, activities: IEntity[]): number {
    const actIndex = this.getControl(i, 'activityIndex').value;
    return activities.find((a) => a.index === actIndex)?.batchSize;
  }

  calcNoProdOrders(i: number, qty: number, batchSize: number): number {
    const actIndex = this.getControl(i, 'activityIndex').value;
    if (actIndex == null) return 0;

    return batchSize > 0 ? Math.ceil(qty / batchSize) : 1;
  }

  getReportedQty(productIndex: number): number {
    return this.productAndQty.find((p) => p.productIndex === productIndex)?.reportedQty ?? 0;
  }

  toggleUseReportedQtyCheckbox() {
    this.useReportedQty = !this.useReportedQty;
  }

  isReportedQty() {
    return !this.productAndQty.some((x) => x.reportedQty > 0);
  }

  private addStepForm(f: ProductActivityDetails, qty: number, reportedQty: number, sawPOId: number) {
    let activityIndex = f.activitys[0]?.index;
    if (sawPOId != null) {
      activityIndex = f.activityIndex;
    }

    const formGroup = this.fb.group({
      activityIndex: [activityIndex, Validators.required],
      productIndex: [f.productIndex ?? null, Validators.required],
      plannedQty: [qty ?? null, Validators.required],
      reportedQty: [reportedQty ?? null],
    });

    if (f.activitys.length < 1) {
      formGroup.get('activityIndex').disable();
    }

    this.steps.push(formGroup);
  }
}

export class CreateMultiStepData {
  public siteId: Guid;
  public prodOrder: NewProdOrderInputDTO;
  public entityRelations: IEntityRelation[];
  public prodOrderId: number | null;
  public isEmptyStock: boolean;
  public copyPOPreviewParentPOInfo?: ICopyPOPreviewParentPO
}
